import axios from "axios";
import "firebase/auth";
import {
  appVersion,
  clearCookies,
  clearStorages,
  getFirebaseUser,
  getStorage,
} from "../auth/utils";
import { DateTime } from "luxon";

let refreshPromise = null;

axios.interceptors.request.use(
  async (config) => {
    // Ensure headers exist
    config.headers = config.headers || {};

    // Always attach app version (backend reads it)
    config.headers["x-app-version"] = appVersion;

    // Add active club header for multi-club context
    const activeClubId = getStorage().getItem("activeClubId");
    if (activeClubId) {
      config.headers["x-club-id"] = String(activeClubId);
    }

    const isSecureToken =
      typeof config?.url === "string" &&
      config.url.includes("securetoken.googleapis.com");

    const requiresAuth = config.headers["x-requires-auth"] === "true";

    // If this request doesn't require auth OR is a firebase secure token call, skip auth logic.
    if (isSecureToken || !requiresAuth) {
      return config;
    }

    // AUTH LOGIC BELOW (only for authenticated calls)

    let token = getStorage().getItem("token");
    const tokenLastValidity = getStorage().getItem("tokenLastValidity");
    const tokenExpired =
      tokenLastValidity &&
      DateTime.fromMillis(+tokenLastValidity).plus({ minutes: 30 }).toMillis() <
        DateTime.now().toMillis();

    if (!token || tokenExpired) {
      if (!refreshPromise) {
        refreshPromise = getFirebaseUser().then(async (user) => {
          if (!user) throw new Error("No Firebase user");
          const newToken = await user.getIdToken(true);

          getStorage().setItem("token", newToken);
          getStorage().setItem("tokenLastValidity", Date.now().toString());

          return newToken;
        });
      }

      try {
        token = await refreshPromise;
      } catch (error) {
        clearStorages(["token", "user", "refreshToken", "tokenLastValidity"]);
        clearCookies();
        window.location.href = "/login";
        throw new Error("Session expired. Redirecting to login.");
      } finally {
        refreshPromise = null;
      }
    }

    config.headers.Authorization = `Bearer ${token}`;

    // Impersonation support
    const impersonate = getStorage().getItem("impersonate");
    if (impersonate && JSON.parse(impersonate)) {
      config.headers.impersonate = JSON.parse(impersonate).mobile;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error?.response?.status === 401) {
      getStorage().removeItem("token");
      getStorage().removeItem("refreshToken");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

const baseUrl = process.env.REACT_APP_BASE_URL;

export function axiosGraphQL(payload, { noAuth = false } = {}) {
  return axios.post(`${baseUrl}/graphql`, payload, {
    headers: {
      ...(noAuth ? {} : { "x-requires-auth": "true" }), // tells interceptor this request needs auth
    },
  });
}

export function axiosGet(url) {
  return axios.get(`${baseUrl}${url}`);
}

export function axiosPost(url, payload) {
  return axios.post(`${baseUrl}${url}`, payload);
}
